import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Sidebar, { clearSession } from "../components/Sidebar";
import {
  Dashboard6Styles, InlineModal, Field, PagePreloader, StatusChip, PageHeader, KpiStrip, ColumnPicker,
} from "../components/ModalUI";
import * as XLSX from "xlsx";

const PROD_API_BASE = "https://zangon-web-api.azurewebsites.net";
const API_BASE = PROD_API_BASE;
const SESSION_KEY = "zangon_session";

// ==========================================
// HOUSE TYPE IMAGE MAPPING & HELPERS
// ==========================================
const HOUSE_TYPE_IMAGES = {
  "1 Bedroom Bungalow": "/assets/media/zangon/1bedroombungalo.jpg",
  "1 Bedrooms Bungalow": "/assets/media/zangon/1bedroombungalo.jpg",
  "2 Bedroom Bungalow": "/assets/media/zangon/2bedroom.jpg",
  "2 Bedrooms Bungalow": "/assets/media/zangon/2bedroom.jpg",
  "3 Bedrooms Bungalow": "/assets/media/zangon/3bedroomsbungalo.jpg",
  "3 Bedroom Bungalow": "/assets/media/zangon/3bedroomsbungalo.jpg",
  "4 Bedrooms Duplex": "/assets/media/zangon/4bd.jpg",
  "4 Bedroom Bungalow": "/assets/media/zangon/4bedroombungalo.jpg",
  "4 Bedrooms Bungalow": "/assets/media/zangon/4bedroombungalo.jpg",
  "5 Bedrooms Terrace": "/assets/media/zangon/5-light.jpg",
  "6 Bedroom Bungalow": "/assets/media/zangon/6bedroombungalow.jpg",
  "6 Bedrooms Bungalow": "/assets/media/zangon/6bedroombungalow.jpg",
  "6 Bedroom Terrace Duplex": "/assets/media/zangon/6bedroomterraceduple.jpg",
  "6 Bedrooms Terrace Duplex": "/assets/media/zangon/6bedroomterraceduple.jpg",
};

function getHouseImage(typeName) {
  if (!typeName) return "/assets/media/zangon/3bedroomsbungalo.jpg";

  const norm = typeName.toLowerCase().replace(/[^a-z0-9]/g, "").replace(/bedrooms?/g, "bedroom");

  if (norm.includes("1bedroom") || norm.includes("1bd")) {
    return "/assets/media/zangon/1bedroombungalo.jpg";
  }
  if (norm.includes("2bedroom") || norm.includes("2bd")) {
    return "/assets/media/zangon/2bedroom.jpg";
  }
  if (norm.includes("3bedroombungalow") || norm.includes("3bedroombungalo") || norm.includes("3bd")) {
    return "/assets/media/zangon/3bedroomsbungalo.jpg";
  }
  if (norm.includes("4bedroombungalow") || norm.includes("4bedroombungalo")) {
    return "/assets/media/zangon/4bedroombungalo.jpg";
  }
  if (norm.includes("4bedroomduplex") || norm.includes("4bd")) {
    return "/assets/media/zangon/4bd.jpg";
  }
  if (norm.includes("5bedroom")) {
    const isDark = document.documentElement.getAttribute("data-bs-theme") === "dark" || 
                   localStorage.getItem("kt_theme_mode") === "dark";
    return isDark ? "/assets/media/zangon/5-dark.jpg" : "/assets/media/zangon/5-light.jpg";
  }
  if (norm.includes("6bedroombungalow")) {
    return "/assets/media/zangon/6bedroombungalow.jpg";
  }
  if (norm.includes("6bedroomterraceduplex") || norm.includes("6bedroomterraceduple") || (norm.includes("6bedroom") && norm.includes("terrace"))) {
    return "/assets/media/zangon/6bedroomterraceduple.jpg";
  }
  return HOUSE_TYPE_IMAGES[typeName] || "/assets/media/zangon/3bedroomsbungalo.jpg";
}

function safeJsonParse(t) {
  try { return JSON.parse(t); } catch { return null; }
}

function getSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; }
}

export function getAccessToken() { return getSession()?.accessToken || ""; }
export function getRefreshToken() { return getSession()?.refreshToken || ""; }

function incrementLetters(str) {
  let carry = true;
  let res = "";
  for (let i = str.length - 1; i >= 0; i--) {
    let charCode = str.charCodeAt(i);
    if (carry) {
      if (charCode === 90) { res = "A" + res; }
      else if (charCode === 122) { res = "a" + res; }
      else { res = String.fromCharCode(charCode + 1) + res; carry = false; }
    } else {
      res = str[i] + res;
    }
  }
  if (carry) {
    const isUpper = str.charCodeAt(0) >= 65 && str.charCodeAt(0) <= 90;
    res = (isUpper ? "A" : "a") + res;
  }
  return res;
}

function incrementString(str) {
  const numMatch = str.match(/(\d+)$/);
  if (numMatch) {
    const numStr = numMatch[1];
    const prefix = str.slice(0, -numStr.length);
    const nextNum = (parseInt(numStr, 10) + 1).toString();
    return prefix + nextNum.padStart(numStr.length, "0");
  }
  const alphaMatch = str.match(/([a-zA-Z]+)$/);
  if (alphaMatch) {
    const alphaStr = alphaMatch[1];
    return str.slice(0, -alphaStr.length) + incrementLetters(alphaStr);
  }
  return str + "1";
}

// ==========================================
// HTTP & NETWORK UTILITIES
// ==========================================
async function fetchWithTimeout(url, options = {}, ms = 60000) {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), ms);
  if (options.signal) {
    options.signal.addEventListener("abort", () => ctrl.abort());
  }
  try {
    return await fetch(url, { ...options, signal: ctrl.signal });
  } catch (e) {
    if (e?.name === "AbortError") throw new Error("Request aborted.");
    throw e;
  } finally {
    clearTimeout(id);
  }
}

async function refreshAccessToken() {
  const rt = getRefreshToken();
  if (!rt) throw new Error("No refresh token.");
  const res = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: rt }),
  });
  const json = safeJsonParse(await res.text());
  if (!res.ok) throw new Error(json?.message || "Refresh failed.");
  const next = { ...(getSession() || {}), ...(json?.data ?? json ?? {}) };
  localStorage.setItem(SESSION_KEY, JSON.stringify(next));
  localStorage.setItem("auth_token", next?.accessToken || "");
  return next;
}

async function authFetch(url, options = {}) {
  let token = getAccessToken();
  const method = (options.method || "GET").toUpperCase();
  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  };
  if (method !== "GET" && method !== "HEAD") {
    if (!headers["Content-Type"] && !(options.body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }
  } else {
    delete headers["Content-Type"];
  }
  let res = await fetchWithTimeout(url, { ...options, headers });
  if (res.status === 401) {
    try {
      const r = await refreshAccessToken();
      token = r.accessToken;
      headers["Authorization"] = `Bearer ${token}`;
      res = await fetchWithTimeout(url, { ...options, headers });
    } catch {
      clearSession();
      window.location.href = "/sign-in";
      throw new Error("Session expired.");
    }
  }
  return res;
}

async function uploadImage(file, adminId) {
  if (!adminId) throw new Error("Admin ID is missing for upload.");
  const formData = new FormData();
  formData.append("file", file);
  const res = await authFetch(`${API_BASE}/admins/${adminId}/uploads`, {
    method: "POST",
    body: formData,
  });
  const json = safeJsonParse(await res.text());
  if (!res.ok) throw new Error(json?.message || "Failed to upload image.");
  return json?.data;
}

function parseArrayData(json) {
  if (!json) return [];
  const raw = json.data;
  if (raw && typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const parsed = safeJsonParse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && Array.isArray(parsed.data)) return parsed.data;
  }
  if (Array.isArray(json)) return json;
  return [];
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
}

const defaultHouseTypeForm = () => ({
  category: "Bungalow",
  bedrooms: 2,
  label: "",
  imageFile: null,
  imagePreview: "",
});

// ==========================================
// CONFIRM DELETE MODAL
// ==========================================
function ConfirmDeleteModal({ isOpen, title, message, onConfirm, onCancel, confirmLabel = "Delete", isDestructive = true }) {
  return (
    <InlineModal
      open={isOpen}
      icon={isDestructive ? "ki-trash" : "ki-information-5"}
      title={title}
      onClose={onCancel}
      width={460}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={onCancel}>Cancel</button>
          <button type="button" className={`btn d6-mbtn ${isDestructive ? "btn-danger" : "btn-success"}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </>
      }
    >
      <div className="text-gray-700 fs-7" style={{ lineHeight: 1.6 }}>{message}</div>
    </InlineModal>
  );
}

// ==========================================
// DOWNLOAD BUILDINGS MODAL
// ==========================================
function DownloadBuildingsModal({ isOpen, onClose, buildings, buildingTypes, phases }) {
  const [options, setOptions] = useState({
    buildingNumber: true,
    phase: true,
    buildingType: true,
    occupancyStatus: true,
  });
  const [downloading, setDownloading] = useState(false);

  const handleDownload = () => {
    const activeKeys = Object.keys(options).filter((k) => options[k]);
    if (activeKeys.length === 0) {
      toast.error("Please select at least one column to download.");
      return;
    }
    if (buildings.length === 0) {
      toast.error("No buildings to export.");
      return;
    }

    setDownloading(true);
    try {
      const headerMapping = {
        buildingNumber: "Building Number",
        phase: "Phase",
        buildingType: "Building Type",
        occupancyStatus: "Occupancy Status",
      };

      const phaseMap = {};
      phases.forEach((p) => { phaseMap[p.id] = p.name; });
      const typeMap = {};
      buildingTypes.forEach((t) => { typeMap[t.id] = t.name; });

      const rows = buildings.map((b) => {
        const row = {};
        activeKeys.forEach((key) => {
          const col = headerMapping[key];
          switch (key) {
            case "buildingNumber": row[col] = b.number || b.buildingNumber || ""; break;
            case "phase": row[col] = phaseMap[b.phaseId] || ""; break;
            case "buildingType": row[col] = typeMap[b.typeId] || ""; break;
            case "occupancyStatus": row[col] = b.isOccupied || b.tenantId ? "Occupied" : "Vacant"; break;
            default: row[col] = "";
          }
        });
        return row;
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Buildings");
      XLSX.writeFile(wb, `buildings_info_${new Date().toISOString().split("T")[0]}.xlsx`);
      toast.success(`Download started — ${buildings.length} building(s) exported.`);
      onClose();
    } catch (err) {
      toast.error("Failed to generate download.");
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <InlineModal
      open={isOpen}
      icon="ki-file-down"
      title="Download Building Info"
      onClose={onClose}
      width={640}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={onClose} disabled={downloading}>Cancel</button>
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleDownload} disabled={downloading}>
            {downloading ? (
              <><span className="spinner-border spinner-border-sm me-2" />Exporting…</>
            ) : (
              <><i className="ki-outline ki-file-down fs-5 me-2.5 text-white" />Export</>
            )}
          </button>
        </>
      }
    >
      <ColumnPicker options={options} setOptions={setOptions} disabled={downloading} />
    </InlineModal>
  );
}

// ==========================================
// EDIT BUILDING MODAL
// ==========================================
function EditBuildingModal({ isOpen, building, phases, buildingTypes, estateId, onClose, onSuccess, onDelete }) {
  const [loading, setLoading] = useState(false);
  const [modalImgError, setModalImgError] = useState(false);
  const [form, setForm] = useState({
    number: "",
    phaseId: "",
    typeId: "",
  });

  useEffect(() => {
    if (building) {
      setForm({
        number: building.number || building.buildingNumber || "",
        phaseId: building.phaseId || "",
        typeId: building.typeId || "",
      });
      setModalImgError(false);
    }
  }, [building]);

  const currentSelectedType = useMemo(() => {
    return buildingTypes.find((bt) => bt.id === form.typeId);
  }, [buildingTypes, form.typeId]);

  const currentSelectedPhase = useMemo(() => {
    return phases.find((p) => p.id === form.phaseId);
  }, [phases, form.phaseId]);

  const previewImage = useMemo(() => {
    if (!currentSelectedType) return "/assets/media/image.png";
    return currentSelectedType.imageUrl || currentSelectedType.imagePreview || getHouseImage(currentSelectedType.name);
  }, [currentSelectedType]);

  const handleUpdate = async () => {
    if (!estateId || !building?.id) return toast.error("Building information is missing.");
    if (!form.number.trim()) return toast.error("Building number cannot be empty.");
    if (!form.phaseId) return toast.error("Please select a phase.");
    if (!form.typeId) return toast.error("Please select a building type.");

    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/buildings/${building.id}`, {
        method: "PUT",
        body: JSON.stringify({
          number: form.number.trim(),
          phaseId: form.phaseId,
          typeId: form.typeId,
        }),
      });

      const json = safeJsonParse(await res.text());
      if (!res.ok) {
        throw new Error(json?.message || "Failed to update building details.");
      }

      toast.success("Building details updated successfully.");
      onClose();
      onSuccess?.();
    } catch (err) {
      toast.error(err.message || "Failed to update building details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <InlineModal
      open={!!(isOpen && building)}
      icon="ki-home-2"
      title="Building Details & Setup"
      onClose={onClose}
      width={720}
      footer={
        building && (
          <div className="d-flex align-items-center justify-content-between w-100 gap-2 flex-wrap">
            <button
              type="button"
              className="btn btn-light-danger d6-mbtn"
              onClick={() => { onDelete(building.id); onClose(); }}
              disabled={loading}
            >
              <i className="ki-outline ki-trash fs-5 me-2.5" />
              Delete Unit
            </button>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-light d6-mbtn" onClick={onClose} disabled={loading}>Cancel</button>
              <button type="button" className="btn btn-success d6-mbtn" onClick={handleUpdate} disabled={loading}>
                {loading ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</> : "Save Changes"}
              </button>
            </div>
          </div>
        )
      }
    >
      {building && (
        <>
          <div className="rounded-3 overflow-hidden border border-gray-200 bg-light position-relative mb-5" style={{ height: 180 }}>
            {!modalImgError && previewImage ? (
              <img
                src={previewImage}
                alt={currentSelectedType?.name || "House Prototype"}
                className="w-100 h-100 object-fit-cover"
                onError={() => setModalImgError(true)}
              />
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center text-muted h-100">
                <i className="ki-outline ki-picture fs-2x mb-2 text-gray-400" />
                <span className="fs-7 fw-semibold">No Prototype Image Found</span>
              </div>
            )}
          </div>

          <div className="d6m-person">
            <span className="d6m-tile" style={{ width: 52, height: 52 }}><i className="ki-outline ki-home-2" /></span>
            <div className="min-w-0 flex-grow-1">
              <div className="d6m-name text-truncate">{form.number.trim() || building.number || "Building Layout"}</div>
              <div className="d6m-muted text-truncate">Unit ID: {building.id?.slice(0, 8)}…</div>
            </div>
            <StatusChip color="#16A34A">{currentSelectedPhase?.name || "Unassigned Phase"}</StatusChip>
          </div>

          <div className="d6m-kv mt-5">
            <div className="d6m-kv-cell">
              <span className="d6m-k">Assigned phase</span>
              <span className="d6m-v">{currentSelectedPhase?.name || "—"}</span>
            </div>
            <div className="d6m-kv-cell">
              <span className="d6m-k">Building type</span>
              <span className="d6m-v">{currentSelectedType?.name || "—"}</span>
            </div>
          </div>

          <div className="d6m-section">
            <div className="d6m-section-head"><span>Edit details</span></div>
            <div className="d-flex flex-column gap-4">
              <Field label="Building number / identifier" required>
                <input
                  type="text"
                  className="d6m-input"
                  placeholder="e.g. House 12, Block A-001"
                  value={form.number}
                  onChange={(e) => setForm((p) => ({ ...p, number: e.target.value }))}
                />
              </Field>
              <div className="row g-4">
                <Field className="col-6" label="Phase" required>
                  <select className="d6m-input" value={form.phaseId} onChange={(e) => setForm((p) => ({ ...p, phaseId: e.target.value }))}>
                    <option value="" disabled>-- Select Phase --</option>
                    {phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </Field>
                <Field className="col-6" label="Building type" required>
                  <select
                    className="d6m-input"
                    value={form.typeId}
                    onChange={(e) => { setForm((p) => ({ ...p, typeId: e.target.value })); setModalImgError(false); }}
                  >
                    <option value="" disabled>-- Select Building Type --</option>
                    {buildingTypes.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
                  </select>
                </Field>
              </div>
            </div>
          </div>

          <div className="d6m-note">
            <i className="ki-outline ki-information-5"></i>
            <span>Active structural layout linked across resident passes and access logs.</span>
          </div>
        </>
      )}
    </InlineModal>
  );
}

// ==========================================
// MAIN COMPONENT
// ==========================================
export default function EstateSetup() {
  const navigate = useNavigate();
  const session = getSession();
  const estateId = session?.estateId || "";
  const adminId = session?.userId || "";

  const [pageReady, setPageReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const presetEstateName = "Zangon Daura Estate";
  const presetEstateLogo = "/assets/media/zangon-fav.png";

  const [phases, setPhases] = useState([]);
  const [buildingTypes, setBuildingTypes] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Delete confirmation modals
  const [confirmDeletePhase, setConfirmDeletePhase] = useState(null); // { id, name }
  const [confirmDeleteBuildingType, setConfirmDeleteBuildingType] = useState(null); // { id, name }
  const [confirmDeleteBuilding, setConfirmDeleteBuilding] = useState(null); // buildingId string

  // Download buildings modal
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  const [phaseForm, setPhaseForm] = useState({ name: "" });
  const [houseTypeForm, setHouseTypeForm] = useState(defaultHouseTypeForm());

  const [assignForm, setAssignForm] = useState({
    phaseId: "",
    typeId: "",
    prefix: "",
    sequenceStart: "001",
    count: 1,
  });

  useEffect(() => {
    const body = document.body;
    body.id = "kt_app_body";
    body.classList.add("app-default");
    body.setAttribute("data-kt-app-header-fixed-mobile", "true");
    body.setAttribute("data-kt-app-toolbar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-fixed", "true");
    body.setAttribute("data-kt-app-sidebar-push-header", "true");
    body.setAttribute("data-kt-app-sidebar-push-toolbar", "true");
    body.setAttribute("data-kt-app-sidebar-push-footer", "true");

    const html = document.documentElement;
    const saved = localStorage.getItem("kt_theme_mode") || "light";
    html.setAttribute("data-bs-theme", saved);

    const init = () => {
      window.KTThemeMode?.init?.();
      window.KTComponents?.init?.();
      window.KTMenu?.createInstances?.();
      window.KTDrawer?.createInstances?.();
      window.KTScroll?.createInstances?.();
      window.KTScrollTop?.createInstances?.();
    };
    init();
    const t = setTimeout(init, 0);
    return () => clearTimeout(t);
  }, []);

  // Modal overflow behavior
  useEffect(() => {
    if (isEditModalOpen) {
      document.body.classList.add("modal-open");
      document.body.style.overflow = "hidden";
    } else {
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
    }
    return () => {
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
    };
  }, [isEditModalOpen]);

  const fetchData = useCallback(async () => {
    if (!estateId) {
      setIsLoading(false);
      setPageReady(true);
      toast.error("Estate ID is missing. Please log in again.");
      return;
    }

    setIsLoading(true);
    try {
      const [pRes, btRes, bRes] = await Promise.all([
        authFetch(`${API_BASE}/estates/${estateId}/phases`),
        authFetch(`${API_BASE}/estates/${estateId}/building-types`),
        authFetch(`${API_BASE}/estates/${estateId}/buildings`),
      ]);

      const [pJson, btJson, bJson] = await Promise.all([
        pRes.text().then(safeJsonParse),
        btRes.text().then(safeJsonParse),
        bRes.text().then(safeJsonParse),
      ]);

      const fetchedPhases = parseArrayData(pJson);
      const fetchedTypes = parseArrayData(btJson);
      setPhases(fetchedPhases);
      setBuildingTypes(fetchedTypes);
      setBuildings(parseArrayData(bJson));
      setAssignForm((prev) => ({
        ...prev,
        phaseId: prev.phaseId || fetchedPhases[0]?.id || "",
        typeId: prev.typeId || fetchedTypes[0]?.id || "",
      }));
    } catch {
      toast.error("Failed to load estate configuration.");
    } finally {
      setIsLoading(false);
      setPageReady(true);
    }
  }, [estateId]);

  const fetchedEstateId = useRef(null);
  useEffect(() => {
    if (fetchedEstateId.current === estateId) return;
    fetchedEstateId.current = estateId;
    fetchData();
  }, [fetchData, estateId]);

  const stats = useMemo(
    () => ({
      phaseCount: phases.length,
      buildingTypeCount: buildingTypes.length,
      buildingCount: buildings.length,
      activeUnits: buildings.filter((b) => b.isOccupied || b.tenantId).length,
    }),
    [phases, buildingTypes, buildings]
  );

  const getBuildingType = (id) => buildingTypes.find((h) => h.id === id);

  const handleAddPhase = async () => {
    if (!estateId) return toast.error("Estate ID is missing.");
    if (!phaseForm.name.trim()) return toast.error("Please enter a phase name.");
    setIsSubmitting(true);
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/phases`, {
        method: "POST",
        body: JSON.stringify({ name: phaseForm.name.trim() }),
      });
      const json = safeJsonParse(await res.text());
      if (!res.ok) throw new Error(json?.message || "Failed to create phase.");
      toast.success("Phase created successfully.");
      setPhaseForm({ name: "" });
      await fetchData();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePhase = async (phaseId) => {
    if (!estateId) return toast.error("Estate ID is missing.");
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/phases/${phaseId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Phase deleted.");
        setPhases((prev) => prev.filter((p) => p.id !== phaseId));
        setBuildings((prev) => prev.filter((b) => b.phaseId !== phaseId));
      } else if (res.status === 405) {
        throw new Error("Deleting phases isn't supported by the server yet.");
      } else {
        const text = await res.text();
        const json = safeJsonParse(text);
        throw new Error(json?.message || "Failed to delete phase.");
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete phase.");
    } finally {
      setConfirmDeletePhase(null);
    }
  };

  const handleAddBuildingType = async () => {
    if (!estateId) return toast.error("Estate ID is missing.");
    const category = houseTypeForm.category;
    const bedrooms = Number(houseTypeForm.bedrooms);
    if (!category) return toast.error("Please choose a category.");
    const finalName = houseTypeForm.label.trim() || `${bedrooms} Bedroom${bedrooms > 1 ? "s" : ""} ${category}`;
    setIsSubmitting(true);
    try {
      let uploadedImageUrl = null;
      if (houseTypeForm.imageFile) {
        uploadedImageUrl = await uploadImage(houseTypeForm.imageFile, adminId);
      }
      const res = await authFetch(`${API_BASE}/estates/${estateId}/building-types`, {
        method: "POST",
        body: JSON.stringify({ name: finalName, imageUrl: uploadedImageUrl || "" }),
      });
      const json = safeJsonParse(await res.text());
      if (!res.ok) throw new Error(json?.message || "Failed to create building type.");
      const newType = json?.data || json;
      if (uploadedImageUrl && !newType.imageUrl) newType.imageUrl = uploadedImageUrl;
      else if (houseTypeForm.imagePreview && !newType.imageUrl) newType.imagePreview = houseTypeForm.imagePreview;
      setBuildingTypes((prev) => [newType, ...prev]);
      setHouseTypeForm(defaultHouseTypeForm());
      toast.success("Building Type created.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteBuildingType = async (typeId) => {
    if (!estateId) return toast.error("Estate ID is missing.");
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/building-types/${typeId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Building type deleted.");
        setBuildingTypes((prev) => prev.filter((h) => h.id !== typeId));
      } else if (res.status === 409) {
        const text = await res.text();
        const json = safeJsonParse(text);
        const buildingsUsing = json?.data || [];
        if (buildingsUsing.length > 0) {
          const list = buildingsUsing.map((b) => b.buildingNumber || b.number).join(", ");
          toast.error(`Cannot delete. This building type is used by: ${list}`);
        } else {
          toast.error(json?.message || "Cannot delete building type as it is in use.");
        }
      } else {
        const text = await res.text();
        const json = safeJsonParse(text);
        throw new Error(json?.message || "Failed to delete building type.");
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete building type.");
    } finally {
      setConfirmDeleteBuildingType(null);
    }
  };

  const handleDeleteBuilding = async (buildingId) => {
    if (!estateId) return toast.error("Estate ID is missing.");
    try {
      const res = await authFetch(`${API_BASE}/estates/${estateId}/buildings/${buildingId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Building deleted.");
        setBuildings((prev) => prev.filter((b) => b.id !== buildingId));
      } else if (res.status === 405) {
        throw new Error("Deleting buildings isn't supported by the server yet.");
      } else {
        const text = await res.text();
        const json = safeJsonParse(text);
        throw new Error(json?.message || "Failed to delete building.");
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete building.");
    } finally {
      setConfirmDeleteBuilding(null);
    }
  };

  const handleAddBuilding = async () => {
    if (!estateId) return toast.error("Estate ID is missing.");
    const { phaseId, typeId, prefix, sequenceStart, count } = assignForm;
    if (!phaseId) return toast.error("Select a phase.");
    if (!typeId) return toast.error("Select a building type.");
    if (!sequenceStart.trim()) return toast.error("Start sequence cannot be empty.");
    const numCount = parseInt(count, 10);
    if (isNaN(numCount) || numCount < 1) return toast.error("Count must be at least 1.");
    if (numCount > 100) return toast.error("You can only generate up to 100 buildings at a time.");
    setIsSubmitting(true);
    let successCount = 0;
    let currentSequence = sequenceStart.trim();
    try {
      for (let i = 0; i < numCount; i++) {
        const finalNumber = `${prefix || ""}${currentSequence}`.trim();
        const res = await authFetch(`${API_BASE}/estates/${estateId}/buildings`, {
          method: "POST",
          body: JSON.stringify({ phaseId, typeId, number: finalNumber }),
        });
        if (!res.ok) {
          const json = safeJsonParse(await res.text());
          throw new Error(json?.message || `Failed at creating building: ${finalNumber}.`);
        }
        successCount++;
        currentSequence = incrementString(currentSequence);
      }
      toast.success(`Successfully added ${successCount} building(s).`);
      setAssignForm((prev) => ({ ...prev, sequenceStart: currentSequence, count: 1 }));
    } catch (e) {
      toast.error(e.message || "An error occurred during bulk creation.");
    } finally {
      setIsSubmitting(false);
      await fetchData();
    }
  };

  const openEditBuildingModal = (building) => {
    setSelectedBuilding(building);
    setIsEditModalOpen(true);
  };

  const closeEditBuildingModal = () => {
    setIsEditModalOpen(false);
    setSelectedBuilding(null);
  };

  const handleLogout = (e) => {
    e.preventDefault();
    clearSession();
    navigate("/sign-in");
  };

  if (!adminId) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center vh-100">
        <h3 className="text-danger">Admin ID missing – please log in again.</h3>
        <button className="btn btn-success mt-3" onClick={() => navigate("/sign-in")}>Go to Sign In</button>
      </div>
    );
  }

  return (
    <>
      <Dashboard6Styles />

      <PagePreloader visible={!pageReady} />

      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
          <div id="kt_app_header" className="app-header" data-kt-sticky="true" data-kt-sticky-activate="{default: false, lg: true}" data-kt-sticky-name="app-header-sticky" data-kt-sticky-offset="{default: false, lg: '300px'}">
            <div className="app-container container-fluid d-flex flex-stack align-items-center my-6" id="kt_app_header_container">
              <div className="d-flex align-items-center d-block d-lg-none ms-n3">
                <div className="btn btn-icon btn-active-color-primary w-35px h-35px me-2" id="kt_app_sidebar_mobile_toggle">
                  <i className="ki-outline ki-abstract-14 fs-2" />
                </div>
                <Link to="/dashboard"><img alt="Logo" src="/assets/media/zangon-fav.png" className="h-30px" /></Link>
              </div>
              
              <PageHeader title="Estate Setup">
                <button
                  type="button"
                  className="btn btn-success d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold"
                  onClick={() => setIsDownloadModalOpen(true)}
                  disabled={isLoading}
                  title="Download Building Info"
                >
                  <i className="ki-outline ki-file-down fs-6 me-sm-2 text-white" />
                  <span className="d-none d-sm-inline">Download Building Info</span>
                </button>
              </PageHeader>
            </div>
          </div>

          <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
            <Sidebar collapsible />

            <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
              <div className="d-flex flex-column flex-column-fluid">
                <div id="kt_app_content" className="app-content flex-column-fluid">
                  <div id="kt_app_content_container" className="app-container container-fluid">

                    {!estateId && (
                      <div className="alert alert-dismissible bg-light-danger d-flex flex-column flex-sm-row p-5 mb-10 rounded-3 border border-danger border-dashed">
                        <i className="ki-outline ki-information fs-2hx text-danger me-4 mb-5 mb-sm-0" />
                        <div className="d-flex flex-column pe-0 pe-sm-10">
                          <h4 className="fw-semibold text-danger">Missing Estate Configuration</h4>
                          <span>You are not linked to an Estate ID. Please{" "}
                            <a href="#" onClick={handleLogout} className="fw-bold text-danger text-decoration-underline">log out</a>{" "}and sign in again.
                          </span>
                        </div>
                      </div>
                    )}

                    <KpiStrip
                      loading={isLoading}
                      items={[
                        { icon: "ki-map", label: "Phases", value: stats.phaseCount, sub: "Estate phases" },
                        { icon: "ki-home-2", label: "Building Types", value: stats.buildingTypeCount, sub: "Archetype templates" },
                        { icon: "ki-bank", label: "Total Buildings", value: stats.buildingCount, sub: "Built structures" },
                        { icon: "ki-profile-circle", label: "Registered Houses", value: stats.activeUnits, sub: "Active units" },
                      ]}
                    />

                    <div className="row g-5 align-items-stretch" style={{ minHeight: "calc(100vh - 320px)" }}>
                      <div className="col-12 col-xl-4" style={{ display: "flex", flexDirection: "column" }}>
                        <div className="d6-card d-flex flex-column overflow-hidden" style={{ flex: 1 }}>
                          <div className="p-6 flex-grow-1" style={{ overflowY: "auto" }}>

                            {/* Preset Estate Overview Card */}
                            <div className="p-6 bg-light border-bottom border-gray-200 mb-6 mx-n6 mt-n6">
                              <div className="d-flex align-items-center gap-4">
                                <div 
                                  className="bg-transparent d-flex align-items-center justify-content-center flex-shrink-0 rounded-circle overflow-hidden border border-2 border-gray-200"
                                  style={{ width: "65px", height: "65px", minWidth: "65px", minHeight: "65px" }}
                                >
                                  <img
                                    src={presetEstateLogo}
                                    alt={presetEstateName}
                                    className="rounded-circle"
                                    style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                      if (e.currentTarget.nextElementSibling) {
                                        e.currentTarget.nextElementSibling.style.display = "flex";
                                      }
                                    }}
                                  />
                                  <span
                                    className="fw-bold fs-2 text-primary bg-light-primary w-100 h-100 align-items-center justify-content-center rounded-circle"
                                    style={{ display: "none" }}
                                  >
                                    {presetEstateName ? presetEstateName.charAt(0).toUpperCase() : "E"}
                                  </span>
                                </div>
                                
                                <div className="d-flex flex-column">
                                  <span className="card-label fw-bold text-gray-900 fs-3">{presetEstateName}</span>
                                  <div className="mt-1 d-flex align-items-center text-gray-600 fs-7 fw-semibold">
                                    <i className="ki-outline ki-geolocation fs-6 text-gray-500 me-1" />
                                    <span>Kado, Federal Capital Territory</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="mb-6">
                              <div className="d6-title text-gray-900 mb-3">Create Phase</div>
                              <div className="d-flex gap-2">
                                <input className="d6m-input" value={phaseForm.name} onChange={(e) => setPhaseForm({ name: e.target.value })} placeholder="e.g. Phase 1" disabled={!estateId} onKeyDown={(e) => e.key === "Enter" && handleAddPhase()} />
                                <button className="btn btn-success d6-mbtn" onClick={handleAddPhase} disabled={isSubmitting || !estateId}>
                                  {isSubmitting ? <span className="spinner-border spinner-border-sm" /> : "Add"}
                                </button>
                              </div>
                            </div>

                            <div className="separator my-6" />

                            <div className="mb-6">
                              <div className="d6-title text-gray-900 mb-3">Define Building Type</div>
                              <div className="row g-3">
                                <div className="col-12 col-md-6">
                                  <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">Category</label>
                                  <select className="d6m-input" value={houseTypeForm.category} onChange={(e) => setHouseTypeForm((p) => ({ ...p, category: e.target.value }))} disabled={!estateId}>
                                    <option value="Bungalow">Bungalow</option>
                                    <option value="Terrace">Terrace</option>
                                    <option value="Duplex">Duplex</option>
                                    <option value="Apartment">Apartment</option>
                                  </select>
                                </div>
                                <div className="col-12 col-md-6">
                                  <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">Bedrooms</label>
                                  <select className="d6m-input" value={houseTypeForm.bedrooms} onChange={(e) => setHouseTypeForm((p) => ({ ...p, bedrooms: Number(e.target.value) }))} disabled={!estateId}>
                                    {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n} Bedroom</option>)}
                                  </select>
                                </div>
                               <div className="col-12">
                                  <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">Image</label>
                                  <div className="d6m-upload" style={{ opacity: estateId ? 1 : 0.6 }}>
                                    <label htmlFor="building-type-image" className="d6m-upload-preview mb-0" style={{ borderRadius: 12, cursor: estateId ? "pointer" : "default" }} aria-label="Upload image">
                                      {houseTypeForm.imagePreview ? <img src={houseTypeForm.imagePreview} alt="" /> : <i className="ki-outline ki-picture"></i>}
                                    </label>
                                    <div className="flex-grow-1 min-w-0">
                                      <div className="d6m-v text-truncate">{houseTypeForm.imageFile?.name || "Building photo"}</div>
                                      <div className="d6m-muted">Optional · PNG or JPG</div>
                                    </div>
                                    <div className="d-flex align-items-center gap-3">
                                      {houseTypeForm.imagePreview && (
                                        <button type="button" className="d6m-link-btn" onClick={() => setHouseTypeForm((p) => ({ ...p, imageFile: null, imagePreview: null }))} disabled={!estateId}>Remove</button>
                                      )}
                                      <label htmlFor="building-type-image" className={`btn btn-light d6-mbtn d6-mbtn-sm mb-0${estateId ? "" : " disabled"}`}>
                                        {houseTypeForm.imagePreview ? "Change" : "Upload"}
                                      </label>
                                    </div>
                                    <input id="building-type-image" type="file" className="d-none" accept="image/*"
                                      onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        e.target.value = "";
                                        if (!file) return;
                                        const preview = await readFileAsDataUrl(file);
                                        setHouseTypeForm((p) => ({ ...p, imageFile: file, imagePreview: preview }));
                                      }}
                                      disabled={!estateId}
                                    />
                                  </div>
                                </div>
                                <div className="col-12 d-flex gap-2 mt-2">
                                  <button className="btn btn-success d6-mbtn w-100" onClick={handleAddBuildingType} disabled={isSubmitting || !estateId}>
                                    {isSubmitting ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="ki-outline ki-plus fs-4 me-2" />}
                                    Create Building Type
                                  </button>
                                  <button className="btn btn-light d6-mbtn" onClick={() => setHouseTypeForm(defaultHouseTypeForm())} disabled={!estateId}>Reset</button>
                                </div>
                              </div>
                            </div>

                            <div className="separator my-6" />

                            <div className="mb-2">
                              <div className="d6-title text-gray-900 mb-3">Create Building in Phase</div>
                              <div className="row g-3">
                                <div className="col-12">
                                  <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">Select Phase</label>
                                  <select className="d6m-input" value={assignForm.phaseId} onChange={(e) => setAssignForm((p) => ({ ...p, phaseId: e.target.value }))} disabled={!estateId || phases.length === 0}>
                                    <option value="" disabled>-- Select Phase --</option>
                                    {phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                  </select>
                                  {phases.length === 0 && !isLoading && <div className="text-muted fs-8 mt-1">No phases yet. Create one above first.</div>}
                                </div>
                                <div className="col-12">
                                  <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">Select Building Type</label>
                                  <select className="d6m-input" value={assignForm.typeId} onChange={(e) => setAssignForm((p) => ({ ...p, typeId: e.target.value }))} disabled={!estateId || buildingTypes.length === 0}>
                                    <option value="" disabled>-- Select Type --</option>
                                    {buildingTypes.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
                                  </select>
                                  {buildingTypes.length === 0 && !isLoading && <div className="text-muted fs-8 mt-1">No building types yet. Create one above first.</div>}
                                </div>
                                <div className="col-12 col-md-4">
                                  <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">Prefix (Optional)</label>
                                  <input className="d6m-input" placeholder="e.g. Block A-" value={assignForm.prefix} onChange={(e) => setAssignForm((p) => ({ ...p, prefix: e.target.value }))} disabled={!estateId} />
                                </div>
                                <div className="col-12 col-md-4">
                                  <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">Start Sequence</label>
                                  <input className="d6m-input" placeholder="e.g. 001, A, A1" value={assignForm.sequenceStart} onChange={(e) => setAssignForm((p) => ({ ...p, sequenceStart: e.target.value }))} disabled={!estateId} />
                                </div>
                                <div className="col-12 col-md-4">
                                  <label className="d-block fs-8 fw-semibold text-gray-700 mb-1">Count</label>
                                  <input type="number" min="1" max="100" className="d6m-input" value={assignForm.count} onChange={(e) => setAssignForm((p) => ({ ...p, count: e.target.value }))} disabled={!estateId} onKeyDown={(e) => e.key === "Enter" && handleAddBuilding()} />
                                </div>
                                <div className="col-12 mt-2">
                                  <button className="btn btn-success d6-mbtn w-100" onClick={handleAddBuilding} disabled={isSubmitting || !estateId || phases.length === 0 || buildingTypes.length === 0}>
                                    {isSubmitting
                                      ? <><span className="spinner-border spinner-border-sm me-2" />Generating...</>
                                      : `Add ${assignForm.count || 1} Building(s) to Estate`}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-xl-8" style={{ display: "flex", flexDirection: "column" }}>
                        <div className="d6-card d-flex flex-column" style={{ flex: 1 }}>
                          <div className="p-6 pb-0 flex-shrink-0">
                            <h3 className="text-gray-900 d6-title mb-1">Estate Structure</h3>
                            <span className="text-gray-400 fs-8">Overview of registered layout</span>
                          </div>

                          <div className="p-6 pt-4">
                            {isLoading ? (
                              <div className="text-center py-10">
                                <span className="spinner-border text-success" />
                              </div>
                            ) : phases.length === 0 ? (
                              <div className="d6m-empty">
                                No phases yet. Start by defining the first phase.
                              </div>
                            ) : (
                              <div className="accordion accordion-flush" id="phasesAccordion">
                                {phases.map((p, index) => {
                                  const phaseBuildings = buildings
                                    .filter((b) => b.phaseId === p.id)
                                    .sort((a, b) => {
                                      const numA = String(a.number || a.buildingNumber || "");
                                      const numB = String(b.number || b.buildingNumber || "");
                                      return numA.localeCompare(numB, undefined, { numeric: true, sensitivity: "base" });
                                    });

                                  return (
                                    <div key={p.id} className="border-bottom border-gray-200 py-4">
                                      {/* Header / Trigger Row */}
                                      <div
                                        className="d-flex align-items-center justify-content-between cursor-pointer user-select-none py-2 collapsed"
                                        data-bs-toggle="collapse"
                                        data-bs-target={`#phase-collapse-${p.id}`}
                                        aria-expanded="false"
                                        aria-controls={`phase-collapse-${p.id}`}
                                      >
                                        <div className="d-flex align-items-center gap-3">
                                          <div>
                                            <div className="d6m-name">{p.name}</div>
                                           
                                          </div>
                                        </div>

                                        <div className="d-flex align-items-center gap-2">
                                          {/* Building count badge per phase */}
                                          <span className="badge bg-light text-gray-700 fs-9 fw-semibold rounded-pill px-2.5 py-1">
                                            {phaseBuildings.length} building{phaseBuildings.length === 1 ? "" : "s"}
                                          </span>
                                          <button
                                            type="button"
                                            className="d6-icon-btn"
                                            title={phaseBuildings.length > 0 ? "Cannot delete: phase still has buildings" : "Delete Phase"}
                                            disabled={phaseBuildings.length > 0}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (phaseBuildings.length > 0) return;
                                              setConfirmDeletePhase({ id: p.id, name: p.name });
                                            }}
                                          >
                                            <i className={`ki-outline ki-trash fs-5 ${phaseBuildings.length > 0 ? "text-gray-400" : "text-danger"}`} />
                                          </button>
                                          <span className="d6-icon-btn">
                                            <i className="ki-outline ki-down fs-5" />
                                          </span>
                                        </div>
                                      </div>

                                      {/* Collapse Content */}
                                      <div
                                        id={`phase-collapse-${p.id}`}
                                        className="collapse"
                                        data-bs-parent="#phasesAccordion"
                                      >
                                        <div
                                          className="pt-4 pb-2"
                                          style={{ maxHeight: "500px", overflowY: "auto", overflowX: "hidden" }}
                                        >
                                          {phaseBuildings.length === 0 ? (
                                            <div className="d6m-empty">
                                              Empty Phase. Add buildings to it.
                                            </div>
                                          ) : (
                                            <div className="row g-4">
                                              {phaseBuildings.map((b) => {
                                                const type = getBuildingType(b.typeId) || {};
                                                const imageUrl = type.imageUrl || type.imagePreview || getHouseImage(type.name);

                                                return (
                                                  <div key={b.id} className="col-12 col-md-6 col-xl-4">
                                                    <button
                                                      type="button"
                                                      onClick={() => openEditBuildingModal(b)}
                                                      className="d6-card border-hover-success h-100 w-100 cursor-pointer text-start transition-all p-0 d-block"
                                                    >
                                                      <div className="p-5 d-flex flex-column w-100">
                                                        <div className="mb-4">
                                                          <div className="d-flex flex-column w-100">
                                                            <div
                                                              className="position-relative rounded-3 bg-light overflow-hidden d-flex align-items-center justify-content-center w-100"
                                                              style={{ height: "180px" }}
                                                            >
                                                              <img
                                                                alt={type.name || "Building"}
                                                                src={imageUrl}
                                                                className="w-100 h-100 object-fit-cover d-block"
                                                              />
                                                              {(b.number || b.buildingNumber) && (
                                                                <div
                                                                  className="position-absolute bottom-0 start-0 m-3 badge bg-white text-success fs-8 fw-semibold rounded-pill px-3 py-1"
                                                                  style={{ zIndex: 1 }}
                                                                >
                                                                  House {b.number || b.buildingNumber}
                                                                </div>
                                                              )}
                                                            </div>
                                                          </div>
                                                        </div>

                                                        <div className="d-flex align-items-center justify-content-between mt-auto pt-2 border-top border-gray-100">
                                                          <div className="d-flex flex-column text-start">
                                                            <div className="d6m-v text-truncate" title={type.name}>
                                                              {type.name || "Standard Layout"}
                                                            </div>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </button>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            <div className="my-8" />

                            <div className="d-flex align-items-center justify-content-between mb-4">
                              <div>
                                <div className="d6-title text-gray-900">Building Type Library</div>
                                <div className="text-gray-400 fs-8">Global configurations used for assignments</div>
                              </div>
                            </div>

                            {buildingTypes.length === 0 ? (
                              <div className="d6m-empty">
                                No templates created.
                              </div>
                            ) : (
                              <div style={{ maxHeight: "350px", overflowY: "auto", paddingRight: "4px" }}>
                              <div className="d-flex flex-column gap-3">
                                {buildingTypes.map((h) => {
                                  const imageUrl = h.imageUrl || h.imagePreview || getHouseImage(h.name);
                                  const usageCount = buildings.filter((b) => b.typeId === h.id).length;
                                  return (
                                    <div
                                      key={h.id}
                                      className="d6-card d6-list-card d-flex align-items-center py-4 px-5 border-hover-success transition-all"
                                      style={{ minHeight: "80px" }}
                                    >
                                      {/* Image on left */}
                                      <div
                                        className="rounded-3 overflow-hidden flex-shrink-0 me-4"
                                        style={{ width: "72px", height: "56px" }}
                                      >
                                        <img
                                          src={imageUrl}
                                          alt={h.name}
                                          className="w-100 h-100 object-fit-cover d-block"
                                          onError={(e) => { e.currentTarget.src = "/assets/media/image.png"; }}
                                        />
                                      </div>

                                      {/* Name */}
                                      <div className="flex-grow-1 overflow-hidden">
                                        <div className="d6m-v text-truncate" title={h.name}>
                                          {h.name}
                                        </div>
                                      </div>

                                      {/* Usage count on right */}
                                      <div className="text-center ms-4 flex-shrink-0" style={{ minWidth: "60px" }}>
                                        <div className="text-gray-400 fs-9 mb-0.5">Units</div>
                                        <div className="fw-semibold text-gray-900 fs-8">{usageCount}</div>
                                      </div>

                                      {/* Delete button */}
                                      <button
                                        type="button"
                                        className="d6-icon-btn flex-shrink-0 ms-4"
                                        title="Remove Building Type"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setConfirmDeleteBuildingType({ id: h.id, name: h.name });
                                        }}
                                      >
                                        <i className="ki-outline ki-trash fs-5 text-danger" />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="my-10" />
                  </div>
                </div>
              </div>

              <div id="kt_app_footer" className="app-footer">
                <div className="app-container container-fluid d-flex justify-content-center align-items-center py-4">
                  <div className="text-gray-900 text-center fs-7">
                    <span className="text-gray-600 fw-semibold me-1">2026&copy;</span>
                    <a href="https://pixels.com.ng/" target="_blank" rel="noreferrer" className="text-gray-600 text-hover-primary fw-bold">Powered by Pixels Solutions Limited</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div id="kt_scrolltop" className="scrolltop bg-success" data-kt-scrolltop="true">
            <i className="ki-outline ki-arrow-up" />
          </div>
        </div>
      </div>

      <EditBuildingModal
        isOpen={isEditModalOpen}
        building={selectedBuilding}
        phases={phases}
        buildingTypes={buildingTypes}
        estateId={estateId}
        onClose={closeEditBuildingModal}
        onSuccess={fetchData}
        onDelete={(buildingId) => setConfirmDeleteBuilding(buildingId)}
      />

      <ConfirmDeleteModal
        isOpen={!!confirmDeletePhase}
        title="Delete Phase"
        message={`Are you sure you want to delete "${confirmDeletePhase?.name}"? This action cannot be undone.`}
        confirmLabel="Delete Phase"
        onConfirm={() => handleDeletePhase(confirmDeletePhase?.id)}
        onCancel={() => setConfirmDeletePhase(null)}
      />

      <ConfirmDeleteModal
        isOpen={!!confirmDeleteBuildingType}
        title="Delete Building Type"
        message={`Are you sure you want to delete "${confirmDeleteBuildingType?.name}"? Make sure it is not assigned to any buildings.`}
        confirmLabel="Delete Type"
        onConfirm={() => handleDeleteBuildingType(confirmDeleteBuildingType?.id)}
        onCancel={() => setConfirmDeleteBuildingType(null)}
      />

      <ConfirmDeleteModal
        isOpen={!!confirmDeleteBuilding}
        title="Delete Building"
        message="Are you sure you want to delete this building unit? This action cannot be undone."
        confirmLabel="Delete Building"
        onConfirm={() => handleDeleteBuilding(confirmDeleteBuilding)}
        onCancel={() => setConfirmDeleteBuilding(null)}
      />

      <DownloadBuildingsModal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        buildings={buildings}
        buildingTypes={buildingTypes}
        phases={phases}
      />
    </>
  );
}